// SHADOW NETWORK — Publish analysis modal.
// Additive. Mounted from ShadowNetworkPage as a sibling of the existing feed.
// PRO users → can publish · FREE users → upgrade prompt · Guests → login prompt.
//
// Refactored (2026-02): heavy panels extracted to ./shadow/publish/* to keep
// this file focused on state + submit orchestration. Behaviour and testids
// are preserved 1:1.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Upload, X as XIcon, Check, AlertTriangle, Crown } from "lucide-react";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { getShadowI18n } from "../i18n/shadow";
import { tFases } from "../i18n/shadow-fases";
import { startProCheckout } from "./shadow/ProGate";
import { MentionTextarea } from "./shadow/MentionTextarea";
import { Field } from "./shadow/publish/Field";
import { ProGatePanel } from "./shadow/publish/ProGatePanel";
import { DropZone } from "./shadow/publish/DropZone";
import { MetaRow } from "./shadow/publish/MetaRow";
import { PollEditor } from "./shadow/publish/PollEditor";
import { PremiumToggle } from "./shadow/publish/PremiumToggle";
import { CoAuthorPanel } from "./shadow/publish/CoAuthorPanel";
import { AIScorePanel } from "./shadow/publish/AIScorePanel";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const IMAGE_MIMES = ["image/jpeg", "image/png", "image/webp"];
const VIDEO_MIMES = ["video/mp4", "video/quicktime", "video/webm", "video/x-matroska"];
const FREE_MAX_CHARS = 500;

export const PublishAnalysisModal = ({
  onPublished,
  // B5b/Shadow — optional pre-fill props for "share-from-chart" flow.
  initialImage = null,       // Blob from htmlToImage capture
  initialTitle = "",
  initialDescription = "",
  initialTicker = "",
  initialTimeframe = "",
  initialDirection = "",
  openSignal = 0,            // bump to force-open the modal externally
}) => {
  const { user, isPro, openLogin } = useAuth();
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const navigate = useNavigate();

  // Modal + PRO gate state
  const [open, setOpen] = useState(false);
  const [proBusy, setProBusy] = useState(false);
  const [proErr, setProErr] = useState(false);
  const [proGateReason, setProGateReason] = useState(null); // "video" | "chars" | null

  // File state
  const [file, setFile] = useState(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const [isVideo, setIsVideo] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  // Editor state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ticker, setTicker] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [direction, setDirection] = useState("");

  // Lifecycle state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // FASE 1.2 — Poll
  const [pollOn, setPollOn] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);

  // FASE 2.5 — AI Score Preview
  const [aiScore, setAiScore] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState(null);

  // FASE 3.2 — Premium toggle
  const [premiumOn, setPremiumOn] = useState(false);

  // FASE 3.5 (3.3) — Co-author invite
  const [coAuthorOn, setCoAuthorOn] = useState(false);
  const [coAuthorUsername, setCoAuthorUsername] = useState("");
  const [coAuthorInviteStatus, setCoAuthorInviteStatus] = useState(null);

  const maxChars = isPro ? 5000 : FREE_MAX_CHARS;

  const reset = () => {
    setFile(null); setIsVideo(false); setTitle(""); setDescription("");
    setTicker(""); setTimeframe(""); setDirection("");
    setLoading(false); setSuccess(false); setError(null); setDragActive(false);
    setPollOn(false); setPollQuestion(""); setPollOptions(["", ""]);
    setAiScore(null); setAiBusy(false); setAiErr(null);
    setPremiumOn(false);
    setCoAuthorOn(false); setCoAuthorUsername(""); setCoAuthorInviteStatus(null);
  };

  const close = () => { setOpen(false); setTimeout(reset, 250); };

  // Hydrate from external pre-fill props (share-from-chart). Runs when
  // openSignal bumps — converts the captured Blob into a File so the
  // existing DropZone/preview/upload pipeline treats it as a normal pick.
  useEffect(() => {
    if (!openSignal) return;
    if (initialImage instanceof Blob) {
      const f = new File([initialImage], `chart-${Date.now()}.png`, { type: "image/png" });
      setFile(f);
      setIsVideo(false);
    }
    if (initialTitle) setTitle(initialTitle);
    if (initialDescription) setDescription(initialDescription);
    if (initialTicker) setTicker(initialTicker.toUpperCase());
    if (initialTimeframe) setTimeframe(String(initialTimeframe).toUpperCase());
    if (initialDirection) setDirection(String(initialDirection).toUpperCase());
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  // -------- File handling --------
  const handleFile = (f) => {
    if (!f) return;
    const video = f.type.startsWith("video/");
    if (video && !isPro) { setProGateReason("video"); return; }
    if (video) {
      if (!VIDEO_MIMES.includes(f.type)) { setError(ui.pam_err_video_type); return; }
      if (f.size > MAX_VIDEO_BYTES) { setError(ui.pam_err_video_size); return; }
    } else {
      if (!IMAGE_MIMES.includes(f.type)) { setError(ui.pam_err_file_type); return; }
      if (f.size > MAX_IMAGE_BYTES) { setError(ui.pam_err_image_size); return; }
    }
    setError(null); setIsVideo(video); setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  // -------- AI Score --------
  const requestAiScore = async () => {
    if (aiBusy) return;
    if (title.trim().length < 3 || description.trim().length < 30) {
      setAiErr(tFases(lang, "publish_ai_min"));
      return;
    }
    setAiErr(null); setAiBusy(true);
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/shadow/ai/score-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          ticker: ticker || null,
          timeframe: timeframe || null,
          direction: direction || null,
        }),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        const msg = data?.detail?.message || data?.detail || "No se pudo obtener la preview";
        throw new Error(typeof msg === "string" ? msg : "Error IA");
      }
      setAiScore(data);
    } catch (err) {
      setAiErr(err.message);
    } finally {
      setAiBusy(false);
    }
  };

  // -------- Trigger --------
  const handleTrigger = () => {
    if (!user) { openLogin(); return; }
    setOpen(true);
  };

  // -------- Submit --------
  const submit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setError(null);
    if (!file) { setError(ui.pam_err_select_file); return; }
    if (title.trim().length < 3) { setError(ui.pam_err_title_min); return; }
    if (title.length > 80) { setError(ui.pam_err_title_max); return; }
    if (description.trim().length < 50) { setError(ui.pam_err_desc_min); return; }
    if (description.length > 5000) { setError(ui.pam_err_desc_max); return; }

    setLoading(true);
    try {
      const token = localStorage.getItem("rebel_jwt");
      const fd = new FormData();
      fd.append("image", file);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      if (ticker.trim()) fd.append("ticker", ticker.trim().toUpperCase());
      if (timeframe) fd.append("timeframe", timeframe);
      if (direction) fd.append("direction", direction);
      if (pollOn && isPro) {
        const q = pollQuestion.trim();
        const opts = pollOptions.map((o) => o.trim()).filter(Boolean);
        if (q.length >= 3 && opts.length >= 2) {
          fd.append("poll_question", q);
          fd.append("poll_options", JSON.stringify(opts));
        }
      }
      const res = await fetch(`${API}/shadow-posts`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
        credentials: "include",
      });
      const data = await safeJson(res);
      if (!res.ok) {
        // Surface the actual backend error so the user (and the dev) can
        // diagnose. Previously this fell back to the generic
        // "Error al publicar" which hid useful info from the network.
        const detail = data?.detail;
        let msg;
        if (detail && typeof detail === "object") {
          msg = detail.message || detail.error || JSON.stringify(detail);
        } else if (typeof detail === "string") {
          msg = detail;
        } else if (typeof data?.error === "string") {
          msg = data.error;
        } else {
          msg = `HTTP ${res.status}${res.statusText ? ` · ${res.statusText}` : ""}`;
        }
        console.error("[PublishAnalysisModal] publish failed:", res.status, data);
        throw new Error(msg);
      }
      setSuccess(true);

      // Premium signal flip post-creation.
      if (premiumOn && isPro && data?.analysis?.id) {
        try {
          await fetch(`${API}/shadow/signals/${data.analysis.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            credentials: "include",
            body: JSON.stringify({ premium: true }),
          });
          data.analysis.premium = true;
        } catch (_e) { /* admin can flip later */ }
      }

      // Co-author invite, fire-and-forget.
      if (coAuthorOn && isPro && coAuthorUsername.trim() && data?.analysis?.id) {
        try {
          const inviteRes = await fetch(`${API}/shadow/co-authors/invite`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            credentials: "include",
            body: JSON.stringify({
              analysis_id: data.analysis.id,
              username: coAuthorUsername.trim().replace(/^@/, "").toLowerCase(),
            }),
          });
          setCoAuthorInviteStatus(inviteRes.ok ? "sent" : "error");
        } catch (_e) { setCoAuthorInviteStatus("error"); }
      }

      try { onPublished?.(data.analysis); } catch (_e) { /* non-critical */ }
      setTimeout(close, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const upgrade = async () => {
    if (proBusy) return;
    setProBusy(true); setProErr("");
    try { await startProCheckout(); }
    catch (err) {
      console.error("[PublishAnalysisModal] checkout failed:", err);
      setProErr(true); setProBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={handleTrigger}
        data-testid="shadow-publish-trigger"
        className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.16em] text-black shadow-[0_8px_24px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(255,140,0,0.55)] md:text-xs"
      >
        <Upload className="h-3.5 w-3.5" />
        <span>{ui.pam_button}</span>
        {!isPro && user && <Crown className="h-3 w-3 text-amber-200/70 opacity-80" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9100] overflow-y-auto bg-black/75 backdrop-blur-md"
            onClick={close}
            data-testid="shadow-publish-modal"
          >
            <div className="flex min-h-full items-start justify-center p-3 sm:items-center sm:p-4 sm:py-10" onClick={close}>
              <motion.div
                initial={{ scale: 0.95, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, y: 10, opacity: 0 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-white/10 bg-zinc-950 text-white shadow-[0_0_80px_rgba(255,140,0,0.18)]"
              >
                <button
                  onClick={close}
                  className="absolute right-4 top-4 z-10 rounded-full border border-white/15 bg-black/70 p-1.5 text-white/75 backdrop-blur hover:border-white/40 hover:text-white"
                  data-testid="shadow-publish-close"
                  aria-label="cerrar"
                >
                  <XIcon className="h-4 w-4" />
                </button>

                {proGateReason ? (
                  <ProGatePanel
                    reason={proGateReason}
                    proBusy={proBusy}
                    proErr={proErr}
                    onUpgrade={upgrade}
                    onBack={() => setProGateReason(null)}
                    ui={ui}
                    freeMaxChars={FREE_MAX_CHARS}
                  />
                ) : (
                  <form onSubmit={submit} className="p-6 sm:p-7">
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-orange-300/80">
                      {ui.pam_eyebrow}
                    </p>
                    <h3 className="mt-1 text-xl font-black tracking-tight">{ui.pam_modal_title}</h3>
                    <p className="mt-1 text-xs text-white/45">{ui.pam_modal_sub}</p>

                    <DropZone
                      file={file}
                      preview={preview}
                      isVideo={isVideo}
                      dragActive={dragActive}
                      setDragActive={setDragActive}
                      onDrop={onDrop}
                      onPick={handleFile}
                      onClear={() => { setFile(null); setIsVideo(false); }}
                      inputRef={inputRef}
                      ui={ui}
                    />

                    {isVideo && (
                      <p className="mt-2 text-[10px] text-cyan-300/70" data-testid="shadow-publish-video-hint">
                        {ui.pam_video_hint}
                      </p>
                    )}

                    <Field label={ui.pam_field_title} counter={`${title.length}/80`} required>
                      <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value.slice(0, 80))}
                        placeholder={ui.pam_field_title_ph}
                        className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm outline-none focus:border-orange-400/50"
                        data-testid="shadow-publish-title"
                        maxLength={80}
                      />
                    </Field>

                    <Field
                      label={ui.pam_field_analysis}
                      counter={`${description.length} / ${maxChars} ${ui.pam_chars_5000}`}
                      counterColor={
                        maxChars - description.length < 50
                          ? "#f87171"
                          : maxChars - description.length < 150
                          ? "#fbbf24"
                          : null
                      }
                      required
                    >
                      <MentionTextarea
                        value={description}
                        onChange={(val) => {
                          if (!isPro && val.length > FREE_MAX_CHARS) {
                            setDescription(val.slice(0, FREE_MAX_CHARS));
                            setProGateReason("chars");
                            return;
                          }
                          setDescription(val.slice(0, maxChars));
                        }}
                        placeholder={ui.pam_field_analysis_ph}
                        rows={10}
                        maxLength={maxChars}
                        className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm outline-none focus:border-orange-400/50"
                        dataTestid="shadow-publish-description"
                      />
                    </Field>
                    {!isPro && description.length >= FREE_MAX_CHARS - 50 && (
                      <button
                        type="button"
                        onClick={() => setProGateReason("chars")}
                        className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-300/85 transition-colors hover:text-amber-200"
                        data-testid="shadow-publish-upgrade-hint"
                      >
                        <Crown className="h-3 w-3" /> Hazte PRO para sin límite (hasta 5000 chars + vídeo)
                      </button>
                    )}

                    <MetaRow
                      ticker={ticker} setTicker={setTicker}
                      timeframe={timeframe} setTimeframe={setTimeframe}
                      direction={direction} setDirection={setDirection}
                      ui={ui}
                    />

                    <PollEditor
                      isPro={isPro} lang={lang}
                      pollOn={pollOn} setPollOn={setPollOn}
                      pollQuestion={pollQuestion} setPollQuestion={setPollQuestion}
                      pollOptions={pollOptions} setPollOptions={setPollOptions}
                      onProGate={setProGateReason}
                    />

                    <PremiumToggle
                      isPro={isPro} lang={lang}
                      premiumOn={premiumOn} setPremiumOn={setPremiumOn}
                      onProGate={setProGateReason}
                    />

                    <CoAuthorPanel
                      isPro={isPro} lang={lang}
                      coAuthorOn={coAuthorOn} setCoAuthorOn={setCoAuthorOn}
                      coAuthorUsername={coAuthorUsername} setCoAuthorUsername={setCoAuthorUsername}
                      coAuthorInviteStatus={coAuthorInviteStatus}
                      onProGate={setProGateReason}
                    />

                    <AIScorePanel
                      lang={lang}
                      aiScore={aiScore}
                      aiBusy={aiBusy}
                      aiErr={aiErr}
                      onRequest={requestAiScore}
                    />

                    {error && (
                      <div
                        className="mt-4 flex items-start gap-2 rounded-xl border border-rose-500/40 bg-rose-500/[0.08] p-3 text-xs text-rose-200"
                        data-testid="shadow-publish-error"
                      >
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}

                    <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                      <button
                        type="button"
                        onClick={close}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 text-[11px] font-black uppercase tracking-[0.16em] text-white/70 hover:border-white/40 hover:text-white"
                        data-testid="shadow-publish-cancel"
                      >
                        {ui.pam_cancel}
                      </button>
                      <button
                        type="submit"
                        disabled={loading || success}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3 text-[11px] font-black uppercase tracking-[0.16em] text-black shadow-[0_8px_24px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(255,140,0,0.55)] disabled:cursor-wait disabled:opacity-70"
                        data-testid="shadow-publish-submit"
                      >
                        {success ? <><Check className="h-3.5 w-3.5" /> {ui.pam_published}</>
                          : loading ? (isVideo ? ui.pam_processing_video : ui.pam_publishing)
                          : <>{ui.pam_submit}</>}
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PublishAnalysisModal;
